import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Package,
  QrCode,
  CheckCircle,
  Clock,
  AlertTriangle,
  User,
  Calendar,
  MessageSquare,
  Search,
  Filter,
  ArrowLeft,
  CheckCircle2,
  Truck
} from 'lucide-react';
import { Project, ProjectCarton, CartonReceivingRequest } from '../types';
import { apiService } from '../services/api';
import { useAuth, useProject } from '../contexts';
import LoadingSpinner from '../components/LoadingSpinner';
import Scanner from '../components/Scanner';
import { toast } from 'react-hot-toast';
import { cartonCacheService } from '../services/cartonCacheService';
import { cacheService } from '../services/cacheService';
import Button from '../components/Button';
import { ProjectSelector } from '../components/ProjectSelector';

const ReceivingContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing[6]};
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    align-items: stretch;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography['3xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing[2]} 0 0 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme, variant }) => variant === 'primary' ? theme.colors.primary : theme.colors.white};
  color: ${({ theme, variant }) => variant === 'primary' ? theme.colors.white : theme.colors.text};
  border: 1px solid ${({ theme, variant }) => variant === 'primary' ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme, variant }) => variant === 'primary' ? theme.colors.primaryDark : theme.colors.gray50};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[6]};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const CardTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.xl};
  font-weight: ${({ theme }) => theme.typography.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing[4]} 0;
`;

const ScanSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[8]} ${({ theme }) => theme.spacing[4]};
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const ScanIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.primaryBackground};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
`;

const ScanTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: ${({ theme }) => theme.typography.medium};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const ScanDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing[6]} 0;
`;

const CartonDetails = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const CartonCode = styled.h3`
  font-size: ${({ theme }) => theme.typography.xl};
  font-weight: ${({ theme }) => theme.typography.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing[4]} 0;
`;

const DetailsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: ${({ theme }) => theme.typography.medium};
  text-transform: capitalize;
  background: ${({ theme, status }) => {
    switch (status) {
      case 'dispatched': return theme.colors.primaryBackground;
      case 'received': return theme.colors.successBackground;
      default: return theme.colors.gray100;
    }
  }};
  color: ${({ theme, status }) => {
    switch (status) {
      case 'dispatched': return theme.colors.primary;
      case 'received': return theme.colors.success;
      default: return theme.colors.textSecondary;
    }
  }};
`;

const ReceivingForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: ${({ theme }) => theme.typography.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const TextArea = styled.textarea`
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.base};
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const ReceivingActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const RecentReceivingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const ReceivingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const ReceivingInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const ReceivingIcon = styled.div<{ received: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme, received }) => received ? theme.colors.success : theme.colors.warning};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
`;

const ReceivingDetails = styled.div`
  flex: 1;
`;

const ReceivingCartonCode = styled.div`
  font-weight: ${({ theme }) => theme.typography.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const ReceivingTime = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Container = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  max-width: 1200px;
  margin: 0 auto;
`;

const ScannerSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const DetailsSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: ${({ theme }) => theme.typography.semibold};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const InfoItem = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const Value = styled.div`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.base};
  font-weight: ${({ theme }) => theme.typography.medium};
`;

const ProjectBadge = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
    transform: translateY(-1px);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-top: ${({ theme }) => theme.spacing[6]};
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: ${({ theme }) => theme.spacing[2]};
    color: ${({ theme }) => theme.colors.text};
  }
  
  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    max-width: 24rem;
  }
`;

const ProjectInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 4px;
`;

const CartonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const RecentActivity = styled.div`
  margin-top: ${({ theme }) => theme.spacing[8]};
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const ActivityIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primaryBackground};
  color: ${({ theme }) => theme.colors.primary};
`;

const ActivityDetails = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const ActivityTime = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyActivity = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px dashed ${({ theme }) => theme.colors.border};
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ProjectTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.xl};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const ProjectSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing[1]} 0 0 0;
`;

const SwitchButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.typography.medium};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const ProjectStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing[4]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const StatHeader = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography['2xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const StatBreakdown = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CartonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const CartonReceivingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { selectedProject, setSelectedProject } = useProject();
  
  const [showScanner, setShowScanner] = useState(false);
  const [selectedCarton, setSelectedCarton] = useState<ProjectCarton | null>(null);
  const [loading, setLoading] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [recentReceiving, setRecentReceiving] = useState<ProjectCarton[]>([]);
  const [currentCarton, setCurrentCarton] = useState<ProjectCarton | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectData, setProjectData] = useState<any>(null);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debug logging
  useEffect(() => {
    console.log('CartonReceivingPage project state:', { 
      hasSelectedProject: !!selectedProject,
      selectedProject: selectedProject ? { 
        id: selectedProject.id, 
        name: selectedProject.name 
      } : null,
      showScanner,
      projectData: projectData ? { 
        cartons: projectData.cartons?.length,
        status: projectData.status 
      } : null
    });
  }, [selectedProject, showScanner, projectData]);

  // Load projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projectsData = await apiService.getProjects();
        setProjects(projectsData);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Failed to load projects');
      }
    };

    fetchProjects();
  }, []);

  // Load project data when selected - optimized like CartonContentReceivingPage
  useEffect(() => {
    const loadProjectData = async () => {
      if (!selectedProject) {
        setProjectData(null);
        setCurrentCarton(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Use the same efficient pattern as CartonContentReceivingPage
        const projectData = await apiService.getProject(selectedProject.id);
        
        if (projectData) {
          console.log('CartonReceivingPage: Project data loaded:', projectData);
          setProjectData(projectData);
        } else {
          throw new Error('Invalid project data received');
        }
      } catch (error) {
        console.error('CartonReceivingPage: Error loading project data:', error);
        setError('Failed to load project data. Please try again.');
        toast.error('Failed to load project data');
      } finally {
        setLoading(false);
      }
    };

    loadProjectData();
  }, [selectedProject]);

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setShowProjectSelector(false);
    setCurrentCarton(null); // Reset current carton when project changes
  };

  const handleScan = async (barcode: string) => {
    if (!selectedProject) {
      toast.error('Please select a project first');
      setShowProjectSelector(true);
      return;
    }

    try {
      setLoading(true);
      
      // First try to find in the current project data
      let carton = projectData?.cartons?.find((c: any) => c.code === barcode || c.id === barcode);
      
      // If not found, try the carton cache
      if (!carton) {
        carton = await cartonCacheService.getCartonDetails(barcode);
      }
      
      // If still not found, try the API
      if (!carton) {
        carton = await apiService.scanBarcode(barcode, selectedProject.id);
        if (carton) {
          await cartonCacheService.cacheCartonDetails(carton);
        }
      }

      if (carton) {
        setCurrentCarton(carton);
        toast.success('Carton scanned successfully');
      } else {
        toast.error('Carton not found in project');
      }
      
      setShowScanner(false);
    } catch (error) {
      console.error('Error scanning barcode:', error);
      toast.error('Failed to scan carton: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleReceiveCarton = async () => {
    if (!currentCarton || !selectedProject) return;

    try {
      setReceiving(true);
      const request: CartonReceivingRequest = {
        received_by: user?.id ? Number(user.id) : 0,
        remarks: remarks || 'Received via mobile app',
        project_id: selectedProject.id
      };

      await apiService.receiveCarton(currentCarton.code, request);
      
      // Update recent receiving list
      setRecentReceiving(prev => [currentCarton, ...prev.slice(0, 4)]);
      
      toast.success('Carton received successfully');
      setCurrentCarton(null);
      setRemarks('');
    } catch (error) {
      console.error('Error receiving carton:', error);
      toast.error('Failed to receive carton');
    } finally {
      setReceiving(false);
    }
  };

  if (loading && !projectData && !error) {
    return (
      <ReceivingContainer>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh' 
        }}>
          <LoadingSpinner />
        </div>
      </ReceivingContainer>
    );
  }

  if (error) {
    return (
      <ReceivingContainer>
        <Header>
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
            Back
          </BackButton>
          <Title>Delivery</Title>
        </Header>
        <EmptyState>
          {error}
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </EmptyState>
      </ReceivingContainer>
    );
  }

  return (
    <ReceivingContainer>
      <Header>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Back
        </BackButton>
        <Title>Receive Carton</Title>
        
        {selectedProject ? (
          <ProjectBadge onClick={() => setShowProjectSelector(true)}>
            <div style={{ fontWeight: 600 }}>{selectedProject.name}</div>
            {projectData && (
              <ProjectInfo>
                <span>{projectData.cartons?.length || 0} cartons</span>
                <span>•</span>
                <span>{projectData.status || 'Active'}</span>
              </ProjectInfo>
            )}
          </ProjectBadge>
        ) : (
          <Button onClick={() => setShowProjectSelector(true)}>
            Select Project
          </Button>
        )}
      </Header>

      {showProjectSelector && (
        <ProjectSelector
          projects={projects}
          onSelect={handleProjectSelect}
          onClose={() => setShowProjectSelector(false)}
        />
      )}

      {selectedProject ? (
        <>
          <ProjectHeader>
            <div>
              <ProjectTitle>
                {projectData?.name || selectedProject.name}
              </ProjectTitle>
              <ProjectSubtitle>
                {projectData?.customer_name || 'Loading project details...'}
                {projectData?.so_number && ` • SO: ${projectData.so_number}`}
              </ProjectSubtitle>
            </div>
            <SwitchButton>
              Switch Project
            </SwitchButton>
          </ProjectHeader>

          {loading ? (
            <LoadingSpinner />
          ) : projectData ? (
            <>
              <ProjectStats>
                <StatCard>
                  <StatHeader>Total Cartons</StatHeader>
                  <StatValue>{projectData.cartons?.length || 0}</StatValue>
                </StatCard>
                <StatCard>
                  <StatHeader>Received</StatHeader>
                  <StatValue>
                    {projectData.cartons?.filter(c => c.status === 'received').length || 0}
                  </StatValue>
                  <StatBreakdown>
                    <span>Total: {projectData.cartons?.length || 0}</span>
                  </StatBreakdown>
                </StatCard>
                <StatCard>
                  <StatHeader>Pending</StatHeader>
                  <StatValue>
                    {projectData.cartons?.filter(c => c.status !== 'received').length || 0}
                  </StatValue>
                </StatCard>
                <StatCard>
                  <StatHeader>Total Panels</StatHeader>
                  <StatValue>
                    {projectData?.statistics?.panels?.total_panels || 
                     (projectData?.panels?.length || 0) || 
                     (projectData.cartons || []).reduce((sum, c) => sum + (c.panel_items || 0), 0)}
                  </StatValue>
                </StatCard>
                <StatCard>
                  <StatHeader>Total Hardware</StatHeader>
                  <StatValue>
                    {projectData?.statistics?.hardware?.total_hardware || 
                     (projectData?.hardware?.length || 0) || 
                     (projectData.cartons || []).reduce((sum, c) => sum + (c.hw_items || 0), 0)}
                  </StatValue>
                </StatCard>
              </ProjectStats>

              <CartonGrid>
                {projectData.cartons?.map((carton) => (
                  <Card key={carton.id}>
                    <CartonHeader>
                      <div>Carton {carton.code}</div>
                      <div>
                        {carton.status === 'received' ? (
                          <CheckCircle color="green" />
                        ) : carton.received_at ? (
                          <Clock color="orange" />
                        ) : (
                          <AlertTriangle color="red" />
                        )}
                      </div>
                    </CartonHeader>
                    <CartonDetails>
                      <DetailItem>
                        <Package size={16} />
                        <span>{carton.panel_items || 0} panels</span>
                      </DetailItem>
                      <DetailItem>
                        <Package size={16} />
                        <span>{carton.hw_items || 0} hardware items</span>
                      </DetailItem>
                      {carton.received_at && (
                        <DetailItem>
                          <Calendar size={16} />
                          <span>Received {new Date(carton.received_at).toLocaleDateString()}</span>
                        </DetailItem>
                      )}
                    </CartonDetails>
                  </Card>
                ))}
              </CartonGrid>
            </>
          ) : (
            <EmptyState>
              Failed to load project data. Please try again.
            </EmptyState>
          )}
        </>
      ) : (
        <EmptyState>
          <Package size={48} />
          <h3>No Project Selected</h3>
          <p>Please select a project to receive cartons</p>
          <Button onClick={() => setShowProjectSelector(true)}>
            Select Project
          </Button>
        </EmptyState>
      )}

      {showScanner && (
        <Scanner
          onClose={() => setShowScanner(false)}
          onScan={handleScan}
          title="Scan Carton QR Code"
        />
      )}
    </ReceivingContainer>
  );
};

export default CartonReceivingPage;